"use client"

import { useEffect, useRef } from 'react'

/**
 * Article Expert Help — partner Q&A embed (FRIDGE-74, funded-backlog
 * "Ask-a-Fridge-Tech" test; standing rule: "NO forum").
 *
 * DARK-BUILD CONTRACT:
 * - `NEXT_PUBLIC_JUSTANSWER_PARTNER_ID` absent/empty → the component returns
 *   null. Zero DOM, zero script, zero trace in HTML. The module goes live
 *   only when the owner sets the env var at partnership activation.
 * - Mounted ONLY on the curated repair-intent article set
 *   (lib/repair-intent-articles.ts) via the article template — never
 *   sitewide, never a forum, never any community surface.
 * - The partner's embed script is loaded lazily, client-side, ONLY when this
 *   module actually mounts — no sitewide script tag, no preload.
 * - Disclosure is ALWAYS rendered with the module ("Ad · Expert answers by a
 *   partner service" + the site-standard affiliate-commission sentence from
 *   components/affiliate-disclaimer.tsx / site-footer.tsx). Integrity rule:
 *   our copy never names the vendor — the partner's own branding inside the
 *   widget slot is theirs.
 * - Light card chrome only (white background — owner rule), mirroring
 *   ArticleCommercialRouting (.blog-key-specs / .ed-takeaway palette).
 */

const SCRIPT_ID = 'expert-help-partner-script'
const SLOT_ID = 'expert-help-partner-slot'

/**
 * Partner embed loader. UNVERIFIED until partnership activation — the final
 * loader URL comes from the signed partner account and MUST be confirmed
 * before the env var is ever set. Safe while dark: with
 * NEXT_PUBLIC_JUSTANSWER_PARTNER_ID unset this component renders nothing and
 * this URL is never requested.
 */
const PARTNER_EMBED_SRC = 'https://www.justanswer.com/partner/embed.js'

function ExpertHelpEmbed({ partnerId }: { partnerId: string }) {
  const slotRef = useRef<HTMLDivElement>(null)

  // Lazy-load the partner script only when the module mounts (dedupe by id —
  // client-side navigation between repair articles must not stack scripts).
  useEffect(() => {
    if (document.getElementById(SCRIPT_ID)) return
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = `${PARTNER_EMBED_SRC}?pid=${encodeURIComponent(partnerId)}`
    script.setAttribute('data-partner-id', partnerId)
    document.body.appendChild(script)
  }, [partnerId])

  return (
    <section
      aria-label="Expert help"
      style={{
        background: '#fff',
        border: '1px solid var(--gray-200, #e5e7eb)',
        borderTop: '3px solid var(--fridge-orange, #ff9951)',
        borderRadius: 12,
        padding: '20px 24px',
        margin: '36px 0 0',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}
    >
      {/* Ad disclosure label — always rendered with the module. */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--gray-500, #6b7280)',
          marginBottom: 8,
        }}
      >
        Ad · Expert answers by a partner service
      </div>
      <div
        style={{
          fontSize: 19,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
          color: '#111827',
          marginBottom: 6,
        }}
      >
        Stuck on this repair?
      </div>
      <p
        style={{
          fontSize: 14.5,
          lineHeight: 1.55,
          color: '#4b5563',
          margin: '0 0 14px',
        }}
      >
        Connect one-on-one with an appliance-repair expert about the exact
        symptoms you&apos;re seeing.
      </p>
      {/* The partner script populates this slot with its own widget (the
          partner's branding inside the slot is theirs). */}
      <div
        id={SLOT_ID}
        ref={slotRef}
        data-partner-id={partnerId}
        style={{ minHeight: 120 }}
      />
      {/* Site-standard affiliate disclosure sentence (same voice as
          components/affiliate-disclaimer.tsx and the footer disclosure). */}
      <p
        style={{
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--gray-500, #6b7280)',
          margin: '12px 0 0',
        }}
      >
        When you connect with an expert through this module, we may earn an
        affiliate commission. This does not impact our reviews and comparisons.
      </p>
    </section>
  )
}

/**
 * Env-gated wrapper — the ONLY export the template mounts. Absent/empty
 * partner-ID env var → null (zero DOM). Kept hook-free so the null path
 * renders nothing under any renderer.
 */
export function ArticleExpertHelp() {
  const partnerId = process.env.NEXT_PUBLIC_JUSTANSWER_PARTNER_ID?.trim()
  if (!partnerId) return null
  return <ExpertHelpEmbed partnerId={partnerId} />
}
